import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild
} from "@angular/core";
import { BehaviorSubject, from, of, Subject, EMPTY, fromEvent } from "rxjs";
import {
  concatMap,
  map,
  switchMap,
  takeUntil,
  skip,
  mapTo,
  tap
} from "rxjs/operators";
import { ExtendedMediaStream, mapStreamToAudio } from "youm-lib";
import { ConferenceStream } from "youm-lib/conference";

@Component({
  selector: "app-audio",
  templateUrl: "./audio.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AudioComponent implements AfterViewInit, OnDestroy {
  @ViewChild("audioRef") _audioRef!: ElementRef<HTMLAudioElement>;

  @Input() set isMuted(value: null | boolean) {
    this._isMuted$.next(value);
  }

  @Input() set stream(value: null | Stream) {
    this._stream$.next(value);
  }

  @Input() set deviceId(value: null | string) {
    this._deviceId$.next(value);
  }

  private readonly _deviceId$ = new BehaviorSubject<null | string>(null);
  private readonly _isMuted$ = new BehaviorSubject<null | boolean>(null);
  private readonly _stream$ = new BehaviorSubject<null | Stream>(null);
  private readonly _rawStream$ =
    new BehaviorSubject<null | ExtendedMediaStream>(null);
  private readonly _destroy$ = new Subject<void>();

  ngAfterViewInit() {
    const audio = this._audioRef.nativeElement;

    // In order to handle
    // AbortError: The play() request was interrupted by a new load request.
    // for the stream without the audio track
    fromEvent(audio, "canplay")
      .pipe(
        concatMap(() => audio.play()),
        takeUntil(this._destroy$)
      )
      .subscribe();

    this._stream$
      .pipe(
        switchMap((_) => (_ && "stream$" in _ ? _.stream$ : of(_))),
        tap((_) => this._rawStream$.next(_)),
        takeUntil(this._destroy$)
      )
      .subscribe();

    this._rawStream$
      .pipe(
        tap(() => (audio.srcObject = null)),
        // setSinkId should work in a synchronous context
        concatMap((_) =>
          from(this.setSinkId(this._deviceId$.value)).pipe(mapTo(_))
        ),
        switchMap((_) =>
          _
            ? mapStreamToAudio(_, audio).pipe(
                // 'load' call fires the 'canplay' event
                tap(() => audio.load())
              )
            : EMPTY
        ),
        takeUntil(this._destroy$)
      )
      .subscribe();

    this._deviceId$
      .pipe(
        // setSinkId should not interrupt `mapStreamToAudio` call.
        skip(1),
        concatMap((_) => this.setSinkId(_)),
        takeUntil(this._destroy$)
      )
      .subscribe();
  }

  ngOnDestroy() {
    const audio = this._audioRef.nativeElement;

    this._stream$.complete();
    this._rawStream$.complete();

    this._destroy$.next();
    this._destroy$.complete();

    // Explicitly destroy audio's media stream,
    // otherwise it would play sound even without the 'audio' element within the DOM.
    audio.pause();
    audio.srcObject = null;
    audio.load();
  }

  get isMuted$() {
    return this._isMuted$.pipe(map((_) => !!_));
  }

  private async setSinkId(deviceId: null | string) {
    if (!deviceId) {
      return;
    }

    const audioRef = this._audioRef.nativeElement;
    if (!audioRef) {
      return;
    }

    if (!audioRef.setSinkId) {
      return;
    }

    return audioRef.setSinkId(deviceId).catch((_) => _);
  }
}

type Stream = ExtendedMediaStream | ConferenceStream;
